import React, { useCallback, useEffect, useState } from 'react';
import { FirstMenu } from '../../models/first-menu.entity';
import i18n from '../../i18n';
import GlobalNotification from '../../component/common/snackbar';
import { apiManage_rbp } from '../../request';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import _ from 'lodash';
import { RBP_MENU_SETTINGS } from '../../constants/rbp-menu-settings.constant';
import './scss/park-mode-first-menu.scss';

export type ParkModeFirstMenuProps = {
    /** 一级菜单绑定保存成功后刷新子表 */
    afterOnSave: () => void | Promise<void>;
};

/**
 * 娱乐项目「一级菜单」绑定：设置 id=148，与额外费用(147)、绑定费用(1)互斥占用同一批一级菜单。
 * 无 Card：区块标题 + 横向选项；点击即 PATCH，实时生效。
 */
const ParkModeFirstMenuSetting: React.FC<ParkModeFirstMenuProps> = ({ afterOnSave }) => {
    const [menus, setMenus] = useState<FirstMenu[]>([]);
    const [selectedId, setSelectedId] = useState<number | undefined>();
    const [disabledIds, setDisabledIds] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const init = useCallback(async () => {
        setIsLoading(true);
        const getMenu = apiManage_rbp.get_menus_v3({ with_dish: false });
        const getBind = apiManage_rbp.getSettingById(RBP_MENU_SETTINGS.BIND_COST);
        const getExtra = apiManage_rbp.getSettingById(RBP_MENU_SETTINGS.EXTRA_COST);
        const getPark = apiManage_rbp.getSettingById(RBP_MENU_SETTINGS.PARK_MODE);
        try {
            const [menusRes, bindRes, extraRes, parkRes] = await Promise.all([
                getMenu,
                getBind,
                getExtra,
                getPark,
            ]);
            let nextMenus: FirstMenu[] = [];
            if (!_.get(menusRes, 'error')) {
                nextMenus = menusRes as FirstMenu[];
            } else if (_.get(menusRes, 'message') !== 'canceled') {
                GlobalNotification.error(i18n.t('kwa_setting_page_get_first_level_menu_failed'));
            }
            const bindId = !_.get(bindRes, 'error')
                ? (_.get(bindRes, '[0].value.menu_id', undefined) as number | undefined)
                : undefined;
            const extraId = !_.get(extraRes, 'error')
                ? (_.get(extraRes, '[0].value.menu_id', undefined) as number | undefined)
                : undefined;
            const parkId = !_.get(parkRes, 'error')
                ? (_.get(parkRes, '[0].value.menu_id', undefined) as number | undefined)
                : undefined;
            const disabled = [bindId, extraId].filter((v) => v !== undefined && v !== null) as number[];
            setMenus(nextMenus);
            setSelectedId(parkId);
            setDisabledIds(disabled);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void init();
    }, [init]);

    const persistMenuId = useCallback(
        async (menuId: number) => {
            const settingResult = await apiManage_rbp.patchSettingById(RBP_MENU_SETTINGS.PARK_MODE, {
                init: true,
                menu_id: menuId,
            });
            if (_.get(settingResult, 'error')) {
                GlobalNotification.error(i18n.t('global_text_operate_failure'));
                return false;
            }
            GlobalNotification.success(i18n.t('global_text_operate_success'));
            await afterOnSave();
            return true;
        },
        [afterOnSave],
    );

    const handlePickMenu = async (menuId: number) => {
        if (disabledIds.includes(menuId)) {
            return;
        }
        if (selectedId === menuId) {
            return;
        }
        const previousId = selectedId;
        setSelectedId(menuId);
        const ok = await persistMenuId(menuId);
        if (!ok) {
            setSelectedId(previousId);
        }
    };

    return (
        <Box className="park-mode-first-menu">
            <Box className="park-mode-first-menu__surface">
                <Typography variant="subtitle1" component="h2" className="park-mode-first-menu__title">
                    {i18n.t('rwa_setting_page_first_menu')}
                </Typography>
                <Box className="park-mode-first-menu__choices-wrap">
                    {isLoading ? (
                        <Box className="park-mode-first-menu__skeleton-wrap">
                            <Skeleton variant="rounded" height={56} width={200} />
                            <Skeleton variant="rounded" height={56} width={200} />
                            <Skeleton variant="rounded" height={56} width={200} />
                        </Box>
                    ) : (
                        menus.map((item) => {
                            const id = item.main_group_id as number;
                            const isSelected = selectedId === id;
                            return (
                                <Button
                                    key={id}
                                    type="button"
                                    variant="contained"
                                    disableElevation
                                    disabled={disabledIds.includes(id)}
                                    className={
                                        isSelected
                                            ? 'park-mode-first-menu__choice park-mode-first-menu__choice--selected'
                                            : 'park-mode-first-menu__choice park-mode-first-menu__choice--default'
                                    }
                                    onClick={() => void handlePickMenu(id)}
                                >
                                    {item.main_group_name}
                                </Button>
                            );
                        })
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default ParkModeFirstMenuSetting;
